import React, { useState, useRef, useCallback, useEffect } from "react";
import styled from "styled-components";
import {
  Container,
  Wrapper,
  Title,
  Desc,
  TimelineWrapper,
  TimelineContent,
  TimelineSide
} from "./ExperienceStyle";
import ExperienceCard from "../Cards/ExperienceCard";
import { experiences } from "../../data/constants";

function useActiveMilestone(cardCount) {
  const [activeIdx, setActiveIdx] = useState(0);
  const cardRefs = useRef([]);

  const setRef = useCallback(idx => el => {
    cardRefs.current[idx] = el;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = cardRefs.current.map(ref => {
        if (!ref) return Infinity;
        const rect = ref.getBoundingClientRect();
        return Math.abs(rect.top - window.innerHeight / 2);
      });
      const minIdx = offsets.indexOf(Math.min(...offsets));
      setActiveIdx(minIdx);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [cardCount]);

  return [activeIdx, setRef];
}

const TimelineAnimation = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(30, 30, 60, 0.55);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.22);
  backdrop-filter: blur(10px) saturate(180%);
  border-radius: 32px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const Experience = () => {
  const [activeIdx, setRef] = useActiveMilestone(experiences.length);

  return (
    <Container id="experience">
      <Wrapper>
        <Title>🚀 Professional Journey</Title>
        <Desc>Building the Future, One Project at a Time</Desc>
        <TimelineWrapper>
          <TimelineContent>
            {experiences.map((experience, idx) => (
              <div ref={setRef(idx)} key={idx}>
                <ExperienceCard experience={experience} active={activeIdx === idx} />
              </div>
            ))}
          </TimelineContent>
          <TimelineSide>
            <TimelineAnimation>
              {/* Enhanced floating achievements */}
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${20 + (i % 6) * 50}px`,
                    top: 0,
                    animation: `floatIcon${i} 8s linear ${i * 0.7}s infinite`,
                    zIndex: 2,
                    fontSize: `${28 + (i % 3) * 6}px`,
                    filter: 'drop-shadow(0 0 20px #7F7FD5) drop-shadow(0 0 40px #86A8E7)',
                    opacity: 0.95,
                    pointerEvents: 'none',
                    transition: 'transform 0.4s cubic-bezier(.4,2,.3,1)',
                  }}
                >
                  <div style={{
                    background: 'linear-gradient(135deg, #7F7FD5 40%, #86A8E7 70%, #91EAE4 100%)',
                    borderRadius: '50%',
                    padding: '12px',
                    boxShadow: '0 4px 24px #7F7FD5',
                    border: '2px solid rgba(255,255,255,0.3)',
                    display: 'inline-block',
                    backdropFilter: 'blur(8px)',
                    transform: `rotate(${(i * 30) % 360}deg)`,
                  }}>{['🏆','🚀','💼','⭐','🎯','🧠','💡','📈','🔗','📝','🎨','⚡'][i % 12]}</div>
                </div>
              ))}

              {/* Progress indicators */}
              <div style={{
                position: 'absolute',
                top: '30%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 3,
                textAlign: 'center',
                color: 'rgba(255,255,255,0.9)',
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '900',
                  background: 'linear-gradient(135deg, #7F7FD5, #86A8E7, #91EAE4)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px',
                  textShadow: '0 4px 24px rgba(127, 127, 213, 0.5)',
                }}>
                  {activeIdx + 1}/{experiences.length}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.8)',
                  textShadow: '0 2px 12px rgba(127, 127, 213, 0.3)',
                }}>
                  Milestones Achieved
                </div>
              </div>

              {/* Skill progress rings */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '20%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: '4px solid rgba(127, 127, 213, 0.2)',
                  borderTop: '4px solid #7F7FD5',
                  animation: 'spin 3s linear infinite',
                  boxShadow: '0 0 20px rgba(127, 127, 213, 0.3)',
                }}></div>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  color: '#7F7FD5',
                }}>💪</div>
              </div>

              <div style={{
                position: 'absolute',
                top: '50%',
                right: '20%',
                transform: 'translate(50%, -50%)',
                zIndex: 3,
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: '4px solid rgba(134, 168, 231, 0.2)',
                  borderTop: '4px solid #86A8E7',
                  animation: 'spin 4s linear infinite reverse',
                  boxShadow: '0 0 20px rgba(134, 168, 231, 0.3)',
                }}></div>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  color: '#86A8E7',
                }}>🔥</div>
              </div>

              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                ${[...Array(12)].map((_, i) => `
                  @keyframes floatIcon${i} {
                    0% { top: -80px; opacity: 0.6; transform: scale(0.8); }
                    10% { opacity: 1; transform: scale(1); }
                    90% { opacity: 1; transform: scale(1); }
                    100% { top: 120%; opacity: 0.3; transform: scale(0.9); }
                  }
                `).join('')}
              `}</style>

              <div style={{
                position: 'absolute',
                bottom: '32px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'rgba(255,255,255,0.95)',
                fontWeight: 800,
                fontSize: '1.4rem',
                textShadow: '0 3px 24px #7F7FD5',
                pointerEvents: 'none',
                zIndex: 2,
                letterSpacing: '0.04em',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))',
                borderRadius: '24px',
                padding: '16px 40px',
                boxShadow: '0 4px 32px rgba(127, 127, 213, 0.3)',
                border: '2px solid rgba(127, 127, 213, 0.4)',
                backdropFilter: 'blur(8px)',
                animation: 'pulse 2s ease-in-out infinite',
              }}>
                ✨ Your Journey ✨
              </div>
            </TimelineAnimation>
          </TimelineSide>
        </TimelineWrapper>
      </Wrapper>
    </Container>
  );
};

export default Experience;
